import { TwoFactorAuthentication } from '@/legacy/features/user/twoFactorAuthentication/twoFactorAuthentication';
import { Action, QueryState } from '@/legacy/utils/reactHelpers';

const localConfig = {
  /**
   * How many digits is the code that is sent to the user (exact digits)
   */
  exactCodeLength: 6,
};

enum ActionType {
  ResendCode = 'ResendCode',
  BeginCallAuthentication = 'BeginCallAuthentication',
  SelectTwoFactorAuthentication = 'SelectTwoFactorAuthentication',
  SetIsAuthenticationStarted = 'SetIsAuthenticationStarted',
  SetErrorMessage = 'SetErrorMessage',
  SetIsDefaultTwoFactorAuthenticationMode = 'SetIsDefaultTwoFactorAuthenticationMode',
  SetIsLoading = 'SetIsLoading',
  SetIsOpen = 'SetIsOpen',
  SetIsRememberDevice = 'SetIsRememberDevice',
  SetTwoFactorAuthentications = 'SetTwoFactorAuthentications',
  SubmitCode = 'SubmitCode',
  UpdateCode = 'UpdateCode',

  QueryBeginAuthentication = 'QueryBeginAuthentication',
  QueryGetTwoFactorAuthentications = 'QueryGetTwoFactorAuthentications',
  QuerySubmitCode = 'QuerySubmitCode',
  QueryBeginCallAuthentication = 'QueryBeginCallAuthentication',
}

/**
 * The state of the hook.
 */
interface State extends QueryState<ActionType> {
  /**
   * The two-factor authentication code.
   */
  code: string;
  /**
   * Any message to display to the user in the case of an unhappy flow.
   */
  errorMessage: string;
  /**
   * Used to track whether the default or alternative two-factor authentication should be used.
   */
  isDefaultTwoFactorAuthenticationMode: boolean;
  /**
   * Tracks loading state.
   */
  isLoading: boolean;
  /**
   * Tracks whether the dialog is displayed.
   */
  isOpen: boolean;
  /**
   * Lets the user select this device as a "trusted" device.
   */
  isRememberDevice: boolean;
  /**
   * The default method or the method chosen by the user.
   */
  selectedTwoFactorAuthentication: TwoFactorAuthentication;
  /**
   * A list of all authentication methods associated with this user.
   */
  twoFactorAuthentications: Array<TwoFactorAuthentication>;
}

function getInitialState(initializer?: Partial<State>): State {
  const initialState: State = {
    code: '',
    errorMessage: '',
    isDefaultTwoFactorAuthenticationMode: true,
    isLoading: true,
    isOpen: false,
    isRememberDevice: false,
    queries: [],
    selectedTwoFactorAuthentication: undefined,
    twoFactorAuthentications: [],
  };

  if (initializer) {
    return { ...initialState, ...initializer };
  }

  return initialState;
}

function reducer(state: State, action: Action<ActionType>): State {
  switch (action.type) {
    case ActionType.ResendCode:
      return {
        ...state,
        queries: [
          {
            type: ActionType.QueryBeginAuthentication,
            payload: state.selectedTwoFactorAuthentication?.id,
          },
        ],
      };

    case ActionType.SelectTwoFactorAuthentication: {
      const selectedTwoFactorAuthentication = action.payload as TwoFactorAuthentication;

      return {
        ...state,
        selectedTwoFactorAuthentication,
        errorMessage: null, // Clear any previous error message during MFA selection
        code: null,
        queries: [
          {
            type: ActionType.QueryBeginAuthentication,
            payload: selectedTwoFactorAuthentication.id,
          },
        ],
      };
    }

    case ActionType.SetIsDefaultTwoFactorAuthenticationMode:
      return {
        ...state,
        isDefaultTwoFactorAuthenticationMode: action.payload,
        errorMessage: null, // Clear any previous error message
        code: null, // Clear any previously introduced OTP code
      };

    case ActionType.SetIsRememberDevice:
      return {
        ...state,
        isRememberDevice: action.payload,
      };

    case ActionType.SetErrorMessage:
      return {
        ...state,
        errorMessage: action.payload,
        isLoading: false,
      };

    case ActionType.SubmitCode:
      return {
        ...state,
        isLoading: true,
        queries: [
          {
            type: ActionType.QuerySubmitCode,
            payload: {
              id: state.selectedTwoFactorAuthentication?.id,
              rememberDevice: state.isRememberDevice,
              code: state.code,
            },
          },
        ],
      };

    case ActionType.SetIsOpen: {
      const isOpen = action.payload as boolean;

      if (isOpen) {
        return {
          ...state,
          isOpen,
          code: '',
          isLoading: true,
          isRememberDevice: false,
          isDefaultTwoFactorAuthenticationMode: true,
          errorMessage: '',
          queries: [
            {
              type: ActionType.QueryGetTwoFactorAuthentications,
            },
          ],
        };
      }

      return {
        ...state,
        isOpen,
      };
    }

    case ActionType.SetTwoFactorAuthentications: {
      const twoFactorAuthentications = action.payload as Array<TwoFactorAuthentication>;
      const defaultTwoFactorAuthentication = twoFactorAuthentications.find((tfa) => tfa.isDefault);

      const errorMessage = '';
      const isLoading = false;

      if (defaultTwoFactorAuthentication) {
        return {
          ...state,
          errorMessage,
          isLoading,
          isDefaultTwoFactorAuthenticationMode: true,
          selectedTwoFactorAuthentication: defaultTwoFactorAuthentication,
          twoFactorAuthentications,
          queries: [
            {
              type: ActionType.QueryBeginAuthentication,
              payload: defaultTwoFactorAuthentication.id,
            },
          ],
        };
      }

      return {
        ...state,
        errorMessage,
        isLoading,
        twoFactorAuthentications: action.payload,
      };
    }

    case ActionType.UpdateCode: {
      const code = (action.payload as string).replace(/[^0-9]+/g, '');
      if (code.length > localConfig.exactCodeLength) {
        return state;
      }

      return {
        ...state,
        errorMessage: null, // Clear error message when user starts typing
        code,
      };
    }

    case ActionType.BeginCallAuthentication:
      return {
        ...state,
        queries: [
          {
            type: ActionType.QueryBeginCallAuthentication,
            payload: state.selectedTwoFactorAuthentication?.id,
          },
        ],
      };

    default:
      return { ...state };
  }
}

export default reducer;
export type { State };
export { ActionType, getInitialState };
